import { useState } from 'react';
import { useList, useDelete, useInvalidate, useCustomMutation } from '@refinedev/core';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Tag, Copy, ToggleLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DataTable, TableToolbar, type BulkAction, type Column, type SortState } from '@/components/tables';
import { FormHeader } from '@/components/forms';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';
import { notify } from '@/lib/notify';
import { PromoDiscountType } from '@masters-club/types';
import type { PromoCodeItem } from './types';

function formatDate(dateString: string | null): string {
  if (!dateString) { return '—'; }
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function discountTypeLabel(type: PromoCodeItem['discountType']): string {
  switch (type) {
    case PromoDiscountType.PERCENT: return 'Процент';
    case PromoDiscountType.FIXED: return 'Фиксированная';
    case PromoDiscountType.PRICE_OVERRIDE: return 'Замена цены';
    default: return type;
  }
}

function discountValueLabel(item: PromoCodeItem): string {
  if (!item.value) { return '—'; }
  if (item.discountType === PromoDiscountType.PERCENT) { return `${item.value}%`; }
  return item.value;
}

function targetTypeLabel(type: PromoCodeItem['targetType']): string {
  switch (type) {
    case 'ALL': return 'Все';
    case 'COURSE': return 'Курс';
    case 'CHANNEL_PASS': return 'Подписка в канал';
    case 'SUBSCRIPTION_PLAN': return 'Абонемент';
    case 'BOOK': return 'Книга';
    case 'PRICING_TIER': return 'Тариф';
    default: return type;
  }
}

/**
 * mode='codes' — раздел «Промокоды» (классические по коду; фильтр autoApply=false).
 * mode='auto-discounts' — раздел «Скидки» (автоприменяемые; фильтр autoApply=true).
 * Оба используют один backend route /api/promo-codes и одну форму.
 */
interface PromoCodesListProps {
  mode?: 'codes' | 'auto-discounts';
}

export function PromoCodesList({ mode = 'codes' }: PromoCodesListProps = {}) {
  useScrollRestoration();
  const isAuto = mode === 'auto-discounts';
  const keyPrefix = isAuto ? 'discounts' : 'promo-codes';
  const routePrefix = isAuto ? '/discounts' : '/promo-codes';
  const titleLabel = isAuto ? 'Скидки' : 'Промокоды';
  const titleDescription = isAuto
    ? 'Автоприменяемые скидки: считаются в каталоге без ввода кода'
    : 'Управление промокодами для скидок';
  const createButtonLabel = isAuto ? 'Создать скидку' : 'Создать промокод';
  const emptyMessage = isAuto ? 'Нет скидок' : 'Нет промокодов';
  const navigate = useNavigate();
  const [page, setPage] = usePersistedState(`${keyPrefix}:page`, 1);
  const [search, setSearch] = usePersistedState(`${keyPrefix}:search`, '');
  const [sort, setSort] = usePersistedState<SortState | undefined>(`${keyPrefix}:sort`, { field: 'createdAt', order: 'desc' });
  // eslint-disable-next-line unicorn/no-useless-undefined -- usePersistedState requires an explicit initial value
  const [isActiveFilter, setIsActiveFilter] = usePersistedState<boolean | undefined>(`${keyPrefix}:isActiveFilter`, undefined);
  const [deleteTargets, setDeleteTargets] = useState<PromoCodeItem[]>([]);
  const [tableVersion, setTableVersion] = useState(0);
  const pageSize = 20;

  const invalidate = useInvalidate();
  const { mutateAsync: customMutate } = useCustomMutation();

  const filters = [
    ...(search ? [{ field: 'search', operator: 'contains' as const, value: search }] : []),
    ...(isActiveFilter !== undefined ? [{ field: 'isActive', operator: 'eq' as const, value: isActiveFilter }] : []),
    // Срез по типу записи: «Скидки» = autoApply true, «Промокоды» = false.
    { field: 'autoApply', operator: 'eq' as const, value: isAuto },
  ];

  const listResult = useList<PromoCodeItem>({
    resource: 'promo-codes',
    pagination: { pageSize, currentPage: page, mode: 'server' },
    filters,
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  const data = (listResult.query.data?.data ?? []) as PromoCodeItem[];
  const total = listResult.query.data?.total ?? 0;
  const isLoading = listResult.query.isLoading;

  const deleteMutation = useDelete();

  const handleToggleActive = async (items: PromoCodeItem[]) => {
    const allActive = items.every((i) => i.isActive);
    const newValue = !allActive;
    try {
      for (const item of items) {
        await customMutate({
          url: `/promo-codes/${item.id}`,
          method: 'patch',
          values: { isActive: newValue },
        });
      }
      notify.success(newValue ? `Активировано: ${items.length}` : `Деактивировано: ${items.length}`);
      setTableVersion((v) => v + 1);
      invalidate({ resource: 'promo-codes', invalidates: ['list'] });
    } catch {
      notify.error('Ошибка изменения статуса');
    }
  };

  const handleDuplicate = async (items: PromoCodeItem[]) => {
    if (items.length !== 1) {
      notify.info('Для дублирования выберите один промокод');
      return;
    }
    const item = items[0];
    try {
      await customMutate({
        url: '/promo-codes',
        method: 'post',
        values: {
          code: `${item.code}_COPY`,
          discountType: item.discountType,
          value: item.value,
          targetType: item.targetType,
          targetIds: item.targetIds,
          maxActivations: item.maxActivations,
          maxActivationsPerUser: item.maxActivationsPerUser,
          minOrderAmount: item.minOrderAmount,
          startsAt: item.startsAt,
          endsAt: item.endsAt,
          isActive: false,
        },
      });
      notify.success('Промокод дублирован');
      setTableVersion((v) => v + 1);
      invalidate({ resource: 'promo-codes', invalidates: ['list'] });
    } catch {
      notify.error('Ошибка дублирования');
    }
  };

  const handleDelete = async () => {
    if (deleteTargets.length === 0) { return; }

    try {
      for (const item of deleteTargets) {
        await deleteMutation.mutateAsync({ resource: 'promo-codes', id: item.id });
      }
      setDeleteTargets([]);
      setTableVersion((v) => v + 1);
      invalidate({ resource: 'promo-codes', invalidates: ['list'] });
    } catch {
      notify.error('Ошибка удаления');
    }
  };

  const columns: Column<PromoCodeItem>[] = [
    {
      key: 'id',
      header: 'ID',
      width: '60px',
      sortable: true,
      render: (value) => (
        <span className="text-sm text-muted-foreground">#{String(value)}</span>
      ),
    },
    ...(isAuto
      ? []
      : [{
        key: 'code',
        header: 'Код',
        sortable: true,
        render: (_: unknown, item: PromoCodeItem) => (
          <code className="font-mono font-semibold text-sm">{item.code}</code>
        ),
      } satisfies Column<PromoCodeItem>]),
    {
      key: 'discountType',
      header: 'Тип скидки',
      width: '160px',
      sortable: true,
      render: (_, item) => (
        <div className="space-y-0.5">
          <div className="text-sm">{discountTypeLabel(item.discountType)}</div>
          <div className="text-xs text-muted-foreground font-mono">{discountValueLabel(item)}</div>
        </div>
      ),
    },
    {
      key: 'targetType',
      header: 'Применение',
      width: '160px',
      sortable: true,
      render: (_, item) => (
        <span className="text-sm">{targetTypeLabel(item.targetType)}</span>
      ),
    },
    {
      key: 'activationsCount',
      header: 'Активаций',
      width: '130px',
      sortable: true,
      render: (_, item) => (
        <span className="text-sm">
          {item.activationsCount}
          {item.maxActivations !== null ? ` / ${item.maxActivations}` : ''}
        </span>
      ),
    },
    {
      key: 'isActive',
      header: 'Статус',
      width: '110px',
      sortable: true,
      render: (_, item) => (
        <Badge variant={item.isActive ? 'default' : 'outline'}>
          {item.isActive ? 'Активен' : 'Неактивен'}
        </Badge>
      ),
    },
    {
      key: 'endsAt',
      header: 'Срок действия',
      width: '160px',
      sortable: true,
      render: (_, item) => (
        <div className="text-xs text-muted-foreground">
          {item.startsAt || item.endsAt ? (
            <>
              {item.startsAt ? `с ${formatDate(item.startsAt)} ` : ''}
              {item.endsAt ? `по ${formatDate(item.endsAt)}` : ''}
            </>
          ) : (
            '—'
          )}
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: 'Создан',
      width: '120px',
      sortable: true,
      render: (value) => (
        <span className="text-sm text-muted-foreground">{formatDate(String(value))}</span>
      ),
    },
  ];

  const bulkActions: BulkAction<PromoCodeItem>[] = [
    {
      label: 'Редактировать',
      icon: <Edit className="h-4 w-4" />,
      showOnlyWhenSelected: true,
      onClick: (items) => {
        if (items.length !== 1) {
          notify.info('Для редактирования выберите один промокод');
          return;
        }
        navigate(`${routePrefix}/edit/${items[0].id}`);
      },
    },
    {
      label: 'Деактивировать / Активировать',
      icon: <ToggleLeft className="h-4 w-4" />,
      showOnlyWhenSelected: true,
      onClick: (items) => { void handleToggleActive(items); },
    },
    {
      label: 'Дублировать',
      icon: <Copy className="h-4 w-4" />,
      showOnlyWhenSelected: true,
      onClick: (items) => { void handleDuplicate(items); },
    },
    {
      label: 'Удалить',
      icon: <Trash2 className="h-4 w-4" />,
      variant: 'destructive',
      showOnlyWhenSelected: true,
      onClick: setDeleteTargets,
    },
  ];

  return (
    <div className="space-y-6">
      <FormHeader
        title={titleLabel}
        description={titleDescription}
        sticky
        icon={<Tag className="h-5 w-5" />}
        actions={
          <Button onClick={() => navigate(`${routePrefix}/create`)}>
            <Plus className="h-4 w-4 mr-2" />
            {createButtonLabel}
          </Button>
        }
      />

      <TableToolbar
        searchValue={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(1);
        }}
        searchPlaceholder="Поиск по коду..."
        filters={
          <div className="flex gap-2">
            <Button
              type="button"
              variant={isActiveFilter === true ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => {
                setIsActiveFilter(isActiveFilter === true ? undefined : true);
                setPage(1);
              }}
            >
              Активные
            </Button>
            <Button
              type="button"
              variant={isActiveFilter === false ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => {
                setIsActiveFilter(isActiveFilter === false ? undefined : false);
                setPage(1);
              }}
            >
              Неактивные
            </Button>
          </div>
        }
        filtersCount={isActiveFilter !== undefined ? 1 : 0}
        onResetFilters={() => {
          setIsActiveFilter(undefined);
          setPage(1);
        }}
      />

      <DataTable
        key={tableVersion}
        columns={columns}
        data={data}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        sort={sort}
        onSortChange={(newSort) => {
          setSort(newSort);
          setPage(1);
        }}
        onRowClick={(item) => navigate(`${routePrefix}/edit/${item.id}`)}
        emptyMessage={emptyMessage}
        rowKey="id"
        bulkActions={bulkActions}
      />

      <AlertDialog open={deleteTargets.length > 0} onOpenChange={() => setDeleteTargets([])}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteTargets.length === 1
                ? (isAuto ? 'Удалить скидку?' : 'Удалить промокод?')
                : `Удалить ${isAuto ? 'скидок' : 'промокодов'}: ${deleteTargets.length}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Это действие нельзя отменить. Выбранные записи будут удалены навсегда.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { void handleDelete(); }}
              disabled={deleteMutation.mutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.mutation.isPending ? 'Удаление...' : 'Удалить'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
